/**
 * Capacity Allocation — the four-year table (build handover §5). One row per year: baseline,
 * NPI intake, released, held, a bar of held vs ceiling, and percent of ceiling. Any row can be
 * selected as the working year — the position math, the agent read and the resolve target all
 * re-target it. The real `capNumbers`/`heldAt` loop lives in `lib/data/scenario.ts`'s
 * `runScenario`; this component only renders its per-year output.
 */

import type { Dispatch, ReactNode } from "react";

import {
  CAP_YEARS,
  deferFigures,
  type ScenarioContext,
  type ScenarioParams,
  type ScenarioResult,
} from "@/lib/data";
import { cn } from "@/lib/utils";

import type { ScenariosAction } from "./state";

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");

export function CapacityTable({
  draft,
  result,
  ctx,
  dispatch,
}: {
  draft: ScenarioParams;
  result: ScenarioResult;
  ctx: ScenarioContext;
  dispatch: Dispatch<ScenariosAction>;
}) {
  const years = ctx.model.years?.length ? ctx.model.years : CAP_YEARS;
  const baseline = ctx.model.capd.base2026;
  const ceiling = result.ceiling;
  const pinch = years[1] ?? 2027;
  // A year's baseline is the previous year's held (Fix 4); its planned position (held before the
  // levers released anything) is that baseline plus its own intake. `held` / `planned` / `scale`
  // all read this ONE basis so the row, the ghost bar and the axis can't disagree.
  const heldOf = (y: number) => result.held[y] ?? baseline;
  const plannedOf = (i: number, y: number) =>
    (i === 0 ? baseline : heldOf(years[i - 1] as number)) + npiFor(ctx, y);
  const scale = Math.max(ceiling, ...years.map((y, i) => plannedOf(i, y))) * 1.02;
  const w = (v: number) => Math.max(0, Math.min(100, (v / scale) * 100));
  const defer = deferFigures(draft.pacePct, ctx.model);

  return (
    <div className="section" id="sec-capacity" data-spot="hero">
      <div className="section-head">
        <div className="section-title">Capacity Allocation</div>
      </div>
      <div className="section-body">
        <table className="cap-table">
          <thead>
            <tr>
              <th>Year</th>
              <th>Baseline</th>
              <th>NPI intake</th>
              <th>Released</th>
              <th>Held</th>
              <th style={{ width: "42%" }}>Held vs ceiling</th>
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            {years.map((y, i) => {
              const npi = npiFor(ctx, y);
              const held = result.held[y] ?? baseline;
              // Fix 4: the baseline a year builds on is the PREVIOUS year's held, not the flat
              // 2026 figure — otherwise baseline + intake doesn't sum across the row. The first
              // year keeps the site's real 2026 baseline.
              const prevYear = years[i - 1];
              const rowBaseline =
                i === 0 || prevYear === undefined ? baseline : (result.held[prevYear] ?? baseline);
              const released = y >= pinch && y !== years[0] ? result.released : 0;
              const over = held > ceiling;
              const planned = plannedOf(i, y);
              let npiCell: ReactNode = npi ? fmt(npi) : "—";
              if (defer.defer > 0) {
                if (y === pinch) {
                  npiCell = (
                    <>
                      {fmt(npi)} <span className="defer">− {fmt(defer.defer)}</span>
                    </>
                  );
                } else if (defer.next && y === defer.next.year) {
                  npiCell = (
                    <>
                      {fmt(npi)} <span className="defer">+ {fmt(defer.next.amount)}</span>
                    </>
                  );
                } else if (defer.after && y === defer.after.year) {
                  npiCell = (
                    <>
                      {fmt(npi)} <span className="defer">+ {fmt(defer.after.amount)}</span>
                    </>
                  );
                }
              }
              return (
                <tr
                  key={y}
                  className={y === draft.year ? "sel" : undefined}
                  data-spot={`year-${y}`}
                  onClick={() => dispatch({ type: "year", year: y })}
                >
                  <td className="year">
                    {y}
                    {y === pinch ? <span className="pinch-tag">PINCH</span> : null}
                  </td>
                  <td>{fmt(rowBaseline)}</td>
                  <td>{npiCell}</td>
                  <td>{released > 0 ? `− ${fmt(released)}` : "—"}</td>
                  <td>
                    <b>{fmt(held)}</b>
                  </td>
                  <td className="cap-bar-cell">
                    <div className="cap-bar-wrap">
                      {planned > held ? (
                        <div
                          className="cap-bar-ghost"
                          style={{ left: `${w(held)}%`, width: `${w(planned) - w(held)}%` }}
                        />
                      ) : null}
                      <div
                        className={cn("cap-bar-fill", over && "over")}
                        style={{ width: `${w(Math.min(held, ceiling))}%` }}
                      />
                      {over ? (
                        <div
                          className="cap-bar-over"
                          style={{ left: `${w(ceiling)}%`, width: `${w(held) - w(ceiling)}%` }}
                        />
                      ) : null}
                      <div className="cap-bar-val">{fmt(held)}</div>
                      <div
                        className="cap-ceiling-mark"
                        style={{ left: `${w(ceiling)}%` }}
                        title={`ceiling ${fmt(ceiling)}`}
                      />
                    </div>
                  </td>
                  <td
                    className="cap-pct"
                    style={{ color: over ? "var(--jj-red-ink)" : "var(--ink)" }}
                  >
                    {((held / ceiling) * 100).toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="cap-bar-key">
          <span>
            <i style={{ background: "var(--dv4)" }} />
            held
          </span>
          <span>
            <i className="key-over" />
            over the ceiling
          </span>
          <span>
            <i className="key-ghost" />
            released by the levers
          </span>
          <span>
            <i className="key-ceiling" />
            ceiling {fmt(ceiling)}
          </span>
        </div>
      </div>
    </div>
  );
}

function npiFor(ctx: ScenarioContext, year: number): number {
  return ctx.model.npiFamilies.filter((f) => f.rel === year).reduce((a, f) => a + f.total, 0);
}
